import client from './client';
import type { SafeUser, Company, CompanyFilterConfig, SessionEntry, StatusGroup } from '../types/auth';

// ── Users ────────────────────────────────────────────────────────────────────

export async function adminGetUsers(): Promise<SafeUser[]> {
  const { data } = await client.get<SafeUser[]>('/api/admin/users');
  return data;
}

export async function adminCreateUser(payload: {
  username: string;
  password: string;
  role: 'admin' | 'user';
  companyId: string | null;
  displayName: string;
}): Promise<SafeUser> {
  const { data } = await client.post<SafeUser>('/api/admin/users', payload);
  return data;
}

export async function adminUpdateUser(
  id: string,
  payload: Partial<{
    username: string;
    password: string;
    role: 'admin' | 'user';
    companyId: string | null;
    displayName: string;
    active: boolean;
  }>,
): Promise<SafeUser> {
  const { data } = await client.put<SafeUser>(`/api/admin/users/${encodeURIComponent(id)}`, payload);
  return data;
}

export async function adminDeleteUser(id: string): Promise<void> {
  await client.delete(`/api/admin/users/${encodeURIComponent(id)}`);
}

// ── Companies ────────────────────────────────────────────────────────────────

export async function adminGetCompanies(): Promise<Company[]> {
  const { data } = await client.get<Company[]>('/api/admin/companies');
  return data;
}

export async function adminCreateCompany(payload: {
  name: string;
  logoSvg?: string | null;
  groupId?: string | null;
  filterConfig?: Partial<CompanyFilterConfig>;
}): Promise<Company> {
  const { data } = await client.post<Company>('/api/admin/companies', payload);
  return data;
}

export async function adminUpdateCompany(
  id: string,
  payload: Partial<{ name: string; logoSvg: string | null; groupId: string | null; filterConfig: Partial<CompanyFilterConfig> }>,
): Promise<Company> {
  const { data } = await client.put<Company>(`/api/admin/companies/${encodeURIComponent(id)}`, payload);
  return data;
}

export async function adminDeleteCompany(id: string): Promise<void> {
  await client.delete(`/api/admin/companies/${encodeURIComponent(id)}`);
}

// Filter-Vorschau: was würde diese Konfiguration im Zeitraum ergeben?
export interface FilterPreviewSample {
  startDateTime: string;
  von: string;
  an: string;
  inNode: string;
  outNode: string;
  result: string;
}

export interface FilterPreview {
  warming: boolean;
  period: string;
  total: number;
  missed: number;
  oldest: string | null;
  sample: FilterPreviewSample[];
}

export async function adminPreviewCompanyFilter(
  filterConfig: Partial<CompanyFilterConfig>,
  period = 'THIS_MONTH',
): Promise<FilterPreview> {
  const { data } = await client.post<FilterPreview>('/api/admin/companies/preview', { filterConfig, period });
  return data;
}

// ── Status-Gruppen ────────────────────────────────────────────────────────────

export async function adminGetGroups(): Promise<StatusGroup[]> {
  const { data } = await client.get<StatusGroup[]>('/api/admin/groups');
  return data;
}

export async function adminCreateGroup(name: string): Promise<StatusGroup> {
  const { data } = await client.post<StatusGroup>('/api/admin/groups', { name });
  return data;
}

export async function adminRenameGroup(id: string, name: string): Promise<StatusGroup> {
  const { data } = await client.put<StatusGroup>(`/api/admin/groups/${encodeURIComponent(id)}`, { name });
  return data;
}

export async function adminDeleteGroup(id: string): Promise<void> {
  await client.delete(`/api/admin/groups/${encodeURIComponent(id)}`);
}

// ── Sessions ──────────────────────────────────────────────────────────────────

export async function adminGetSessions(): Promise<SessionEntry[]> {
  const { data } = await client.get<SessionEntry[]>('/api/admin/sessions');
  return data;
}

export async function adminKillSession(sessionId: string): Promise<void> {
  await client.delete(`/api/admin/sessions/${encodeURIComponent(sessionId)}`);
}

export async function adminKillUserSessions(userId: string): Promise<void> {
  await client.delete(`/api/admin/sessions/user/${encodeURIComponent(userId)}`);
}

// ── Audit ────────────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  timestamp: string;
  companyId: string | null;
  companyName: string | null;
  userId: string;
  displayName: string;
  callSessionId: string;
  action: 'set_done' | 'unset_done' | 'set_note' | 'clear_note' | 'admin_delete' | 'admin_clear_all';
  value: string;
}

export async function adminGetAuditLog(companyId?: string): Promise<AuditEntry[]> {
  const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  const { data } = await client.get<AuditEntry[]>(`/api/admin/audit${params}`);
  return data;
}

// ── Status / Notizen ───────────────────────────────────────────────────────────

export interface CompanyStatusItem {
  sessionId: string;
  done: boolean;
  note: string;
  updatedBy: string;
  updatedAt: string;
}

export async function adminGetCompanyStatuses(companyId: string): Promise<CompanyStatusItem[]> {
  const { data } = await client.get<CompanyStatusItem[]>(`/api/admin/status/${encodeURIComponent(companyId)}`);
  return data;
}

export async function adminDeleteCompanyStatus(companyId: string, sessionId: string): Promise<void> {
  await client.delete(`/api/admin/status/${encodeURIComponent(companyId)}/${encodeURIComponent(sessionId)}`);
}

export async function adminClearCompanyStatuses(companyId: string): Promise<number> {
  const { data } = await client.delete<{ ok: boolean; count: number }>(`/api/admin/status/${encodeURIComponent(companyId)}`);
  return data.count;
}

// ── Cache ────────────────────────────────────────────────────────────────────

export interface CacheWarmingInfo {
  startedAt: number;
  elapsedMs: number;
  fetchedRows: number;
  oldestFetched: string | null;
  percent: number | null;
  etaSeconds: number | null;
}

export interface CachePeriodEntry {
  period: string;
  node: string | null;
  fieldsCount: number;
  rows: number;
  newest: string | null;
  updatedAt: number;
  freshForMs: number;
  warming: CacheWarmingInfo | null;
}

export interface TickerRun {
  at: number;
  durationMs: number;
  periods: string[];
  newRows: number;
  broadcasts: string[];
}

export interface TickerStatus {
  fastTickMs: number;
  thisMonthEveryNTicks: number;
  fullIntervalMs: number;
  nextTickAt: number;
  nextFullRunAt: number;
  lastFullRun: { at: number; durationMs: number; reason: string } | null;
  stats: { startedAt: number; totalRuns: number; totalNewRows: number; totalBroadcasts: number };
  history: TickerRun[];
}

export async function adminGetTickerStatus(): Promise<TickerStatus> {
  const { data } = await client.get<TickerStatus>('/api/admin/ticker');
  return data;
}

export interface CacheStats {
  plainEntries: number;
  ticker?: TickerStatus;
  periods: CachePeriodEntry[];
}

export async function adminGetCacheStats(): Promise<CacheStats> {
  const { data } = await client.get<CacheStats>('/api/admin/cache');
  return data;
}

export async function adminClearCache(): Promise<void> {
  await client.delete('/api/admin/cache');
}

// ── Stats ────────────────────────────────────────────────────────────────────

export interface CompanyStat {
  companyId: string;
  companyName: string;
  totalTracked: number;
  done: number;
  pending: number;
  auditTotal: number;
  auditRecent: number;
}

export async function adminGetStats(): Promise<CompanyStat[]> {
  const { data } = await client.get<CompanyStat[]>('/api/admin/stats');
  return data;
}
